import { DeliveryStatusEnum } from "@/constants/enums/deliveryStatus.enum";
import { useCallback, useEffect, useReducer } from "react";
import { useSearchParams } from "react-router-dom";
import { DateRange } from "rsuite/esm/DateRangePicker";

export interface ValidatedExpeditionFilters {
  search: string;
  country: string;
  userId: string;
  pickupDateRange: DateRange | null;
  deliveryDateRange: DateRange | null;
  expeditonType: "QUOTATION" | "CUSTOM_QUOTATION" | undefined;
  deliveryStatus?: DeliveryStatusEnum;
  sorting: {
    field: string;
    direction: "ASC" | "DESC";
  };
}

type FilterAction =
  | { type: "SET_SEARCH"; payload: string }
  | { type: "SET_COUNTRY"; payload: string }
  | { type: "SET_USER_ID"; payload: string }
  | {
      type: "SET_PICKUP_DATE_RANGE";
      payload: DateRange | null;
    }
  | {
      type: "SET_DELIVERY_DATE_RANGE";
      payload: DateRange | null;
    }
  | {
      type: "SET_EXPEDITION_TYPE";
      payload: "QUOTATION" | "CUSTOM_QUOTATION" | undefined;
    }
  | {
      type: "SET_SORTING";
      payload: { field: string; direction: "ASC" | "DESC" };
    }
  | { type: "RESET_FILTERS" }
  | { type: "SET_FROM_URL"; payload: Partial<ValidatedExpeditionFilters> }
  | {
      type: "SET_DELIVERY_STATUS";
      payload: DeliveryStatusEnum;
    };

const initialState: ValidatedExpeditionFilters = {
  search: "",
  country: "",
  userId: "",
  pickupDateRange: null,
  deliveryDateRange: null,
  expeditonType: undefined,
  deliveryStatus: undefined,
  sorting: {
    field: "createdAt",
    direction: "DESC",
  },
};

function filterReducer(
  state: ValidatedExpeditionFilters,
  action: FilterAction,
): ValidatedExpeditionFilters {
  switch (action.type) {
    case "SET_SEARCH":
      return { ...state, search: action.payload };
    case "SET_COUNTRY":
      return { ...state, country: action.payload };
    case "SET_USER_ID":
      return { ...state, userId: action.payload };
    case "SET_PICKUP_DATE_RANGE":
      return { ...state, pickupDateRange: action.payload };
    case "SET_DELIVERY_DATE_RANGE":
      return { ...state, deliveryDateRange: action.payload };
    case "SET_EXPEDITION_TYPE":
      return { ...state, expeditonType: action.payload };
    case "SET_SORTING":
      return { ...state, sorting: action.payload };
    case "RESET_FILTERS":
      return initialState;
    case "SET_FROM_URL":
      return { ...state, ...action.payload };
    case "SET_DELIVERY_STATUS":
      return { ...state, deliveryStatus: action.payload };
    default:
      return state;
  }
}

export const useValidatedExpeditionFilters = () => {
  const [_searchParams, setSearchParams] = useSearchParams();
  const [filters, dispatch] = useReducer(filterReducer, initialState);

  // Update URL when filters change
  const updateURL = useCallback(
    (newFilters: ValidatedExpeditionFilters) => {
      const newSearchParams = new URLSearchParams();

      if (newFilters.search) newSearchParams.set("search", newFilters.search);
      if (newFilters.country) newSearchParams.set("country", newFilters.country);
      if (newFilters.userId) newSearchParams.set("userId", newFilters.userId);
      if (newFilters.pickupDateRange) {
        newSearchParams.set("startDate", newFilters.pickupDateRange[0].toISOString());
        newSearchParams.set("endDate", newFilters.pickupDateRange[1].toISOString());
      }
      if (newFilters.deliveryDateRange) {
        newSearchParams.set("deliveryStartDate", newFilters.deliveryDateRange[0].toISOString());
        newSearchParams.set("deliveryEndDate", newFilters.deliveryDateRange[1].toISOString());
      }
      if (newFilters.expeditonType) {
        newSearchParams.set("expeditionType", newFilters.expeditonType);
      }
      if (newFilters.sorting.field !== initialState.sorting.field) {
        newSearchParams.set("sortField", newFilters.sorting.field);
      }
      if (newFilters.sorting.direction !== initialState.sorting.direction) {
        newSearchParams.set("sortDirection", newFilters.sorting.direction);
      }
      if (newFilters.deliveryStatus) {
        newSearchParams.set("deliveryStatus", newFilters.deliveryStatus);
      }

      setSearchParams(newSearchParams, { replace: true });
    },
    [setSearchParams],
  );

  // Action creators with URL updates
  const setSearch = useCallback((search: string) => {
    dispatch({ type: "SET_SEARCH", payload: search });
  }, []);

  const setCountry = useCallback((country: string) => {
    dispatch({ type: "SET_COUNTRY", payload: country });
  }, []);

  const setUserId = useCallback((userId: string) => {
    dispatch({ type: "SET_USER_ID", payload: userId });
  }, []);

  const setPickupDateRange = useCallback((pickupDateRange: DateRange | null) => {
    dispatch({ type: "SET_PICKUP_DATE_RANGE", payload: pickupDateRange });
  }, []);

  const setDeliveryDateRange = useCallback((deliveryDateRange: DateRange | null) => {
    dispatch({ type: "SET_DELIVERY_DATE_RANGE", payload: deliveryDateRange });
  }, []);

  const setExpeditionType = useCallback(
    (expeditionType: "QUOTATION" | "CUSTOM_QUOTATION" | undefined) => {
      dispatch({ type: "SET_EXPEDITION_TYPE", payload: expeditionType });
    },
    [],
  );

  const setSorting = useCallback((field: string, direction: "ASC" | "DESC") => {
    dispatch({ type: "SET_SORTING", payload: { field, direction } });
  }, []);

  const setDeliveryStatus = useCallback((deliveryStatus: DeliveryStatusEnum) => {
    dispatch({ type: "SET_DELIVERY_STATUS", payload: deliveryStatus });
  }, []);

  const resetFilters = useCallback(() => {
    dispatch({ type: "RESET_FILTERS" });
  }, []);

  // Update URL whenever filters change
  useEffect(() => {
    updateURL(filters);
  }, [filters, updateURL]);

  return {
    filters,
    actions: {
      setSearch,
      setCountry,
      setUserId,
      setPickupDateRange,
      setDeliveryDateRange,
      setExpeditionType,
      setSorting,
      resetFilters,
      setDeliveryStatus,
    },
  };
};
